import {
  isToday,
  isYesterday,
  isTomorrow,
  parse,
  format,
  formatDistanceToNowStrict,
  formatISO,
  parseISO,
  set,
  isValid,
  differenceInHours,
  minutesToSeconds,
  secondsToMilliseconds,
  hoursToMinutes,
  subYears,
  isBefore,
} from 'date-fns';
import _ from 'lodash';
import moment from 'moment';
import esLocale from 'date-fns/locale/es';
import ptLocale from 'date-fns/locale/pt';
import enLocale from 'date-fns/locale/en-US';
import { Languages } from 'src/types/translations';
import { Duration } from 'src/types/date';
import i18next from 'i18next';
import 'src/i18n';
import 'src/components/time/i18n';
import { getNavigatorLanguage } from 'src/utils/translations';
import { User } from 'src/types/user';
import { ParamsDatesVacationCell } from 'src/types/vacations';
import { ChatStatus } from 'src/types/chats';
import {
  MILISECONDS,
  MINUTES,
} from 'src/constants/date';

const { EN, ES, PT } = Languages;

export const LOCALE = {
  es: esLocale,
  en: enLocale,
  pt: ptLocale,
};

export const formatRelativeDate = (date) => {
  try {
    if (isToday(date)) return format(date, 'HH:mm');
    if (isYesterday(date)) return i18next.t('GENERAL:YESTERDAY');

    return format(date, 'dd/MM/yy');
  }
  catch (err) {
    return '';
  }
};

export const getCompletedDate = (date, user: User) => {
  const dateToFormat = new Date(date);
  const correctLocale = takeCorrectLocale(user);
  try {
    return format(dateToFormat, 'EEEE PPP', { locale: correctLocale });
  }
  catch (err) {
    return '';
  }
};

export const getNextPostScheduledDate = (date, user: User) => {
  const dateToFormat = new Date(date);
  const languageSymbol = user?.language;
  try {
    if (isToday(dateToFormat)) return i18next.t('GENERAL:TODAY').toLocaleLowerCase();
    if (isTomorrow(dateToFormat)) return i18next.t('GENERAL:TOMORROW').toLocaleLowerCase();

    if (languageSymbol === EN) {
      return format(dateToFormat, 'MM/dd/yyyy');
    }
    return format(dateToFormat, 'dd/MM/yyyy');
  }
  catch (err) {
    return '';
  }
};

export const getTimeFormat = (date) => {
  const dateToFormat = new Date(date);
  try {
    return format(dateToFormat, 'HH:mm a');
  }
  catch (err) {
    return '';
  }
};

export const getFormatedDate = (date, user: User) => {
  const isEnglish = user?.language === EN;
  return format(parseISO(date), isEnglish ? 'MM/dd/yyyy' : 'dd/MM/yyyy');
};

export const formatToTime = (date) => format(date, 'HH:mm');

export const formatDateTime = (date, user: User) => {
  const isEnglish = user?.language === EN;
  return format(parseISO(date), isEnglish ? 'MM/dd/yyyy HH:mm' : 'dd/MM/yyyy HH:mm');
};

export const takeCorrectLocale = (user: User) => {
  let languageSymbol = user?.language;
  if (!languageSymbol) {
    languageSymbol = getNavigatorLanguage();
  }

  if (languageSymbol === ES || languageSymbol === EN || languageSymbol === PT) {
    return LOCALE[languageSymbol];
  }
  return null;
};

export const formatDistanceToNow = (date, user: User, onlyDistance = false) => {
  const correctLocale = takeCorrectLocale(user);
  return i18next.t(onlyDistance ? 'TIME:DISTANCE' : 'TIME:TIME_DISTANCE',
    {
      distance: formatDistanceToNowStrict(
        date,
        { locale: correctLocale },
      ),
    },
  );
};

export const formatDateToLongLocalizedDate = (date, user: User) => {
  const dateToFormat = new Date(date);
  const correctLocale = takeCorrectLocale(user);
  try {
    return format(dateToFormat, 'PPP', { locale: correctLocale });
  }
  catch (err) {
    return '';
  }
};

export const formatDateInChatMessages = (date, user: User) => {
  const dateToFormat = new Date(date);
  const correctLocale = takeCorrectLocale(user);
  try {
    if (isToday(dateToFormat)) return i18next.t('GENERAL:TODAY');
    if (isYesterday(dateToFormat)) return i18next.t('GENERAL:YESTERDAY');

    return format(dateToFormat, 'MMMM d', { locale: correctLocale });
  }
  catch (err) {
    return '';
  }
};

export const formatUTCLocalDate = (
  date: string,
  pattern: string = 'dd/MM/yyyy',
  user: User,
) => {
  const dateToFormat = new Date(date);
  const locale = takeCorrectLocale(user);

  try {
    return format(dateToFormat, pattern, { locale });
  }
  catch (err) {
    return '';
  }
};

export const formatUTCDate = (date: any, pattern = 'dd/MM/yyyy'): string =>
  format(new Date(date), pattern);

export const isSameUTCDate = (a, b): boolean => (
  formatUTCDate(a) === formatUTCDate(b)
);

export const getMonthActual = (user: User) => {
  const dateToFormat = new Date();
  return format(dateToFormat, 'MMMM', { locale: LOCALE[user.language] });
};

export const localDateToSimpleDateString = (date: Date) => formatISO(date, { representation: 'date' });

export const simpleDateStringToLocalDate = (utcISOdate: string) => {
  const dateInUTC = parseISO(utcISOdate);

  const newDate = new Date();
  return set(newDate, {
    year: dateInUTC.getUTCFullYear(),
    month: dateInUTC.getUTCMonth(),
    date: dateInUTC.getUTCDate(),
  });
};

export const orderByDate = (a, b) =>
  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

export const getHiringDate = (date, user) => {
  let formattedDate = '';
  if (date) {
    const dateParsed = parse(date, 'yyyy-MM-dd', new Date());
    if (isValid(dateParsed)) {
      formattedDate = format(dateParsed, 'PP', { locale: LOCALE[user.language] });
    }
  }
  return formattedDate;
};

export const formatDateInVacations = ({ date, user, type, isUtc }:
  ParamsDatesVacationCell) => {
  let dateToFormat;
  if (_.isString(date)) {
    if (isUtc) {
      dateToFormat = new Date(date);
    }
    else {
      dateToFormat = new Date(parseISO(date));
    }
  }
  else {
    dateToFormat = date;
  }
  const correctLocale = takeCorrectLocale(user);
  try {
    return format(dateToFormat, type, { locale: correctLocale });
  }
  catch (err) {
    return '';
  }
};

const getDifferenceInHours = (
  dateLeft?: Date,
  dateRight?: Date,
): number | undefined => {
  const difference = dateLeft
    ? differenceInHours(
      new Date(dateLeft),
      dateRight ? new Date(dateRight) : new Date(),
    )
    : undefined;
  return Math.max(difference, 0);
};

export const getResolutionTime = (
  dueDate: Date,
  createdAt?: Date,
) => {
  const hoursToResolve = getDifferenceInHours(dueDate, createdAt);
  if (!hoursToResolve) {
    return '0h';
  }

  const resolutionTimeDays = Math.trunc(hoursToResolve / 24);
  const resolutionTimeHours = Math.trunc(hoursToResolve % 24);

  let formattedResolutionTime = '';
  if (resolutionTimeDays > 0) {
    formattedResolutionTime += `${resolutionTimeDays}d `;
  }
  if (resolutionTimeHours > 0) {
    formattedResolutionTime += `${resolutionTimeHours}h`;
  }

  return formattedResolutionTime;
};

export const getColorByTime = (createdAt: Date, dueDate: Date, tabSelected: ChatStatus) => {
  if (tabSelected === ChatStatus.UNASSIGNED) {
    return 'secondary';
  }
  else if (tabSelected === ChatStatus.CLOSED) {
    return 'primary';
  }

  const hoursTotalTime = getDifferenceInHours(dueDate, createdAt);
  const hoursToResolve = getDifferenceInHours(dueDate);
  return hoursToResolve > hoursTotalTime * 0.66 ?
    'success' : hoursToResolve > hoursTotalTime * 0.33 ?
      'warning' : 'error';
};

export const getTotalDuration = (duration: Duration) => {
  const newDuration = {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  };

  newDuration.hours += duration?.hours || 0;
  newDuration.minutes += hoursToMinutes(newDuration.hours);

  newDuration.minutes += duration?.minutes || 0;
  newDuration.seconds += minutesToSeconds(newDuration.minutes);

  newDuration.seconds += duration?.seconds || 0;
  newDuration.milliseconds += secondsToMilliseconds(newDuration.seconds);

  return newDuration;
};

export const formatDateDifference = (lastUpdateDate, t) => {
  if (!lastUpdateDate) return null;
  const nowDate = moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSS');

  const diffDate = Math.abs(new Date(lastUpdateDate).getTime() - new Date(nowDate).getTime());

  const minutes = Math.floor(diffDate / MILISECONDS.MINUTE);
  const hours = Math.floor(diffDate / MILISECONDS.HOUR);
  const days = Math.floor(diffDate / MILISECONDS.DAY);

  if (minutes < MINUTES.MINUTE) {
    return t('LESS_THAN_A_MINUTE');
  }
  else if (minutes < MINUTES.HOUR) {
    return t('MINUTES_AGO', { minutes: minutes });
  }
  else if (minutes < MINUTES.DAY) {
    return t('HOURS_AGO', { hours: hours });
  }
  return t('DAYS_AGO', { days: days });
};

export const isMoreThanOneYearOld = (dateString) => {
  if (!dateString) return dateString;
  const oneYearAgo = subYears(new Date(), 1);
  return isBefore(parseISO(dateString), oneYearAgo);
};
